import { StateObservable } from 'redux-observable';
import { Observable } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';

import { IExternalEducationInstance } from '@src/model/externaleducationinstance/ExternalEducationInstance';
import EntityApiServiceRegistry from '@src/service/api/registry/entity/EntityApiServiceRegistry';
import ListFilterBusinessStore from '@src/service/business/common/listFilterBusinessStore';
import { ICollectionData, ICollectionFetchPayload, ILemonAction, IPayloadAction } from '@src/service/business/common/types';
import { createApiResponseUserFeedbackError } from '@src/service/business/common/userFeedbackUtils';
import { actionThunk } from '@src/service/util/observable/operators';
import { startGlobalProgress, stopGlobalProgress } from '@src/service/util/observable/operators';
import { reportCaughtMessage } from '@src/service/util/observable/operators/userFeedback';

// -
// -------------------- Types&Consts

export interface IExternalEducationInstanceListFilter {
  externalEducationTemplate: string;
}

// List filter ID
const EXTERNAL_EDUCATION_INSTANCE_LIST_FILTER = '@@EXTERNAL_EDUCATION_INSTANCE_LIST_FILTER';

// -
// -------------------- Selectors

const getExternalEducationInstanceList = (store: any): ICollectionData<IExternalEducationInstance> => store.externalEducationInstanceList;
const getExternalEducationInstanceListFilter = (store: any): IExternalEducationInstanceListFilter => ListFilterBusinessStore.selectors.getListFilter(store, EXTERNAL_EDUCATION_INSTANCE_LIST_FILTER);

// -
// -------------------- Actions

const Actions = {
  EXTERNAL_EDUCATION_INSTANCE_LIST_FETCH: 'EXTERNAL_EDUCATION_INSTANCE_LIST_FETCH',
  EXTERNAL_EDUCATION_INSTANCE_LIST_LOAD: 'EXTERNAL_EDUCATION_INSTANCE_LIST_LOAD',
  EXTERNAL_EDUCATION_INSTANCE_LIST_CLEAR: 'EXTERNAL_EDUCATION_INSTANCE_LIST_CLEAR',
};

const fetchExternalEducationInstanceList = (payload: ICollectionFetchPayload<IExternalEducationInstanceListFilter>): IPayloadAction<ICollectionFetchPayload<IExternalEducationInstanceListFilter>> => {
  return {
    type: Actions.EXTERNAL_EDUCATION_INSTANCE_LIST_FETCH,
    payload,
  };
};

const loadExternalEducationInstanceList = (data: ICollectionData<IExternalEducationInstance>): IPayloadAction<ICollectionData<IExternalEducationInstance>> => {
  return {
    type: Actions.EXTERNAL_EDUCATION_INSTANCE_LIST_LOAD,
    payload: data,
  };
};

const clearExternalEducationInstanceList = (): ILemonAction => {
  return {
    type: Actions.EXTERNAL_EDUCATION_INSTANCE_LIST_CLEAR,
  };
};

const storeExternalEducationInstanceListFilter = (externalEducationFilter: IExternalEducationInstanceListFilter): ILemonAction => {
  return ListFilterBusinessStore.actions.storeListFilter(EXTERNAL_EDUCATION_INSTANCE_LIST_FILTER, externalEducationFilter);
};

const clearExternalEducationInstanceListFilter = (): ILemonAction => {
  return ListFilterBusinessStore.actions.clearListFilter(EXTERNAL_EDUCATION_INSTANCE_LIST_FILTER);
};

// -
// -------------------- Side-effects

const fetchExternalEducationInstanceListEffect = (action$: Observable<IPayloadAction<ICollectionFetchPayload<IExternalEducationInstanceListFilter>>>, state$: StateObservable<any>) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.EXTERNAL_EDUCATION_INSTANCE_LIST_FETCH;
    }),

    startGlobalProgress(),

    mergeMap((action) => {
      const payload = action.payload;

      return EntityApiServiceRegistry.getService('ExternalEducationInstance')
        .fetchEntityList(payload)
        .pipe(actionThunk(action));
    }),

    stopGlobalProgress(),

    map((data) => {
      return loadExternalEducationInstanceList(data);
    }),

    reportCaughtMessage((error: any) => createApiResponseUserFeedbackError(error, 'EXTERNAL_EDUCATION_INSTANCE.ERROR', 'GENERAL_MESSAGE.GENERAL_FETCH_ERROR')),

    catchError((error: any, o: Observable<any>) => {
      console.error('Error fetching external education instance list', error);
      return o;
    })
  );
};

// -
// -------------------- Reducers

const externalEducationInstanceList = (state: ICollectionData<IExternalEducationInstance> | null = null, action: IPayloadAction<ICollectionData<IExternalEducationInstance>>) => {
  if (action.type === Actions.EXTERNAL_EDUCATION_INSTANCE_LIST_LOAD) {
    return {
      ...action.payload,
    };
  } else if (action.type === Actions.EXTERNAL_EDUCATION_INSTANCE_LIST_CLEAR) {
    return null;
  }

  return state;
};

// --
// -------------------- Business Store

export const ExternalEducationInstanceListBusinessStore = {
  actions: {
    fetchExternalEducationInstanceList,
    clearExternalEducationInstanceList,
    storeExternalEducationInstanceListFilter,
    clearExternalEducationInstanceListFilter,
  },

  selectors: {
    getExternalEducationInstanceList,
    getExternalEducationInstanceListFilter,
  },

  effects: {
    fetchExternalEducationInstanceListEffect,
  },

  reducers: {
    externalEducationInstanceList,
  },
};

// --
// export business store
export default ExternalEducationInstanceListBusinessStore;
